import { useCallback, useEffect, useRef, useState } from 'react'
import { streamPlaceHighlights } from 'tripPlanner/api'

interface UseStreamPlaceHighlightsResult {
  markdown: string
  isStreaming: boolean
  isLoading: boolean
  isError: boolean
  retry: () => void
}

export function useStreamPlaceHighlights(
  googlePlaceId: string | undefined,
  enabled = true,
): UseStreamPlaceHighlightsResult {
  const [markdown, setMarkdown] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [isError, setIsError] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const startStream = useCallback((placeId: string, signal?: AbortSignal) => {
    setMarkdown('')
    setIsStreaming(true)
    setIsError(false)

    streamPlaceHighlights(
      placeId,
      undefined,
      (chunk: string) => {
        setMarkdown((prev) => prev + chunk)
      },
      signal,
    )
      .then(() => {
        setIsStreaming(false)
      })
      .catch((error) => {
        if (error.name === 'AbortError') return
        setIsError(true)
        setIsStreaming(false)
      })
  }, [])

  useEffect(() => {
    if (!enabled || !googlePlaceId) return

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    startStream(googlePlaceId, abortController.signal)

    return () => {
      abortController.abort()
      abortControllerRef.current = null
    }
  }, [enabled, googlePlaceId, startStream])

  const retry = useCallback(() => {
    if (!googlePlaceId) return

    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    startStream(googlePlaceId, abortController.signal)
  }, [googlePlaceId, startStream])

  const isLoading = isStreaming && !markdown

  return {
    markdown,
    isStreaming,
    isLoading,
    isError,
    retry,
  }
}
